import { z } from "zod";
import type { components } from "@/lib/api/types";

type UserResponse = components["schemas"]["UserResponse"];

// Bump when UserResponse gains new required fields the frontend accesses.
// Stale localStorage data will be cleared, forcing a re-login with fresh API data.
const STORAGE_VERSION = 1;

// Validates required fields that downstream components access at render time.
// Optional/nullable fields on UserResponse are intentionally excluded.
const storedUserSchema = z.object({
	version: z.literal(STORAGE_VERSION),
	id: z.number(),
	email: z.string(),
	is_verified: z.boolean(),
	auth_provider: z.string(),
	plan: z.string(),
	checklist_dismissed: z.boolean(),
	memories_intro_dismissed: z.boolean(),
});

const STORAGE_KEYS = {
	ACCESS_TOKEN: "m8tes_access_token",
	REFRESH_TOKEN: "m8tes_refresh_token",
	ACCESS_EXPIRES_AT: "m8tes_access_expires_at",
	REFRESH_EXPIRES_AT: "m8tes_refresh_expires_at",
	USER: "m8tes_user",
} as const;

/**
 * Check if localStorage is available (browser environment)
 */
function isLocalStorageAvailable(): boolean {
	return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

/**
 * Check if there are any auth tokens in localStorage
 * Used to determine if we should show loading screen on app start
 */
export function hasStoredAuth(): boolean {
	if (!isLocalStorageAvailable()) {
		return false;
	}

	try {
		const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
		return !!accessToken;
	} catch {
		return false;
	}
}

export interface StoredAuthData {
	accessToken: string;
	refreshToken: string;
	accessExpiresAt: string;
	refreshExpiresAt: string;
	user: UserResponse;
}

/**
 * Save authentication data to localStorage
 */
export function saveAuthData(data: StoredAuthData): void {
	if (!isLocalStorageAvailable()) {
		return;
	}

	try {
		localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken);
		localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
		localStorage.setItem(STORAGE_KEYS.ACCESS_EXPIRES_AT, data.accessExpiresAt);
		localStorage.setItem(STORAGE_KEYS.REFRESH_EXPIRES_AT, data.refreshExpiresAt);
		localStorage.setItem(
			STORAGE_KEYS.USER,
			JSON.stringify({ version: STORAGE_VERSION, ...data.user })
		);
	} catch (error) {
		console.error("Failed to save auth data to localStorage:", error);
	}
}

/**
 * Load authentication data from localStorage
 */
export function loadAuthData(): StoredAuthData | null {
	if (!isLocalStorageAvailable()) {
		return null;
	}

	try {
		const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
		const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
		const accessExpiresAt = localStorage.getItem(STORAGE_KEYS.ACCESS_EXPIRES_AT);
		const refreshExpiresAt = localStorage.getItem(STORAGE_KEYS.REFRESH_EXPIRES_AT);
		const userJson = localStorage.getItem(STORAGE_KEYS.USER);

		if (!accessToken || !refreshToken || !accessExpiresAt || !refreshExpiresAt || !userJson) {
			return null;
		}

		const parsed = JSON.parse(userJson);
		if (!storedUserSchema.safeParse(parsed).success) {
			clearAuthData();
			return null;
		}

		const { version: _, ...user } = parsed;
		return {
			accessToken,
			refreshToken,
			accessExpiresAt,
			refreshExpiresAt,
			user: user as UserResponse,
		};
	} catch {
		clearAuthData();
		return null;
	}
}

/**
 * Clear all authentication data from localStorage
 */
export function clearAuthData(): void {
	if (!isLocalStorageAvailable()) {
		return;
	}

	try {
		localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
		localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
		localStorage.removeItem(STORAGE_KEYS.ACCESS_EXPIRES_AT);
		localStorage.removeItem(STORAGE_KEYS.REFRESH_EXPIRES_AT);
		localStorage.removeItem(STORAGE_KEYS.USER);
	} catch (error) {
		console.error("Failed to clear auth data from localStorage:", error);
	}
}

/**
 * Get the current access token from localStorage
 */
export function getAccessToken(): string | null {
	if (!isLocalStorageAvailable()) {
		return null;
	}

	try {
		return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
	} catch (error) {
		console.error("Failed to get access token from localStorage:", error);
		return null;
	}
}

/**
 * Get the current refresh token from localStorage
 */
export function getRefreshToken(): string | null {
	if (!isLocalStorageAvailable()) {
		return null;
	}

	try {
		return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
	} catch (error) {
		console.error("Failed to get refresh token from localStorage:", error);
		return null;
	}
}

/**
 * Check if the access token is expired
 */
export function isAccessTokenExpired(): boolean {
	if (!isLocalStorageAvailable()) {
		return true;
	}

	try {
		const expiresAt = localStorage.getItem(STORAGE_KEYS.ACCESS_EXPIRES_AT);
		if (!expiresAt) return true;

		const expirationTime = new Date(expiresAt).getTime();
		const now = Date.now();

		// Consider token expired if it expires in the next 60 seconds
		return now >= expirationTime - 60000;
	} catch (error) {
		console.error("Failed to check access token expiration:", error);
		return true;
	}
}

/**
 * Check if the refresh token is expired
 */
export function isRefreshTokenExpired(): boolean {
	if (!isLocalStorageAvailable()) {
		return true;
	}

	try {
		const expiresAt = localStorage.getItem(STORAGE_KEYS.REFRESH_EXPIRES_AT);
		if (!expiresAt) return true;

		const expirationTime = new Date(expiresAt).getTime();
		const now = Date.now();

		return now >= expirationTime;
	} catch (error) {
		console.error("Failed to check refresh token expiration:", error);
		return true;
	}
}
